<template>
    <div>
        <ul class="uk-tab uk-text-large">
            <li :class="{'uk-active': !current_page||(current_page==='prospective-data')}"><a href @click.prevent="switched_current_page('prospective-data')" ref="prospective-data-link">Prospective</a></li>
            <li :class="{'uk-active': (current_page==='project-data')}"><a href @click.prevent="switched_current_page('project-data')" ref="project-data-link">Project Data</a></li>
            <li :class="{'uk-active': (current_page==='client-data')}"><a href @click.prevent="switched_current_page('client-data')" ref="client-data-link">Client</a></li>
            <li
                v-if="!!record && !!record.computed_last_communication_at"
                class="uk-float-right lam-underline lam-inactive"
            >
                <a href @click.prevent.stop>
                    {{ $moment(record.computed_last_communication_at).fromNow() }}
                </a>
            </li>
        </ul>

        <ul id="prospective-project-pages" class="uk-switcher uk-margin">
            <keep-alive>
                <prospective-data-tab
                    v-if="!current_page||(current_page==='prospective-data')"
                    :record="record"
                    :editable="editable"
                    :wizard-shown="!!page_wizard"
                    class="uk-active"
                />
                <project-data-tab
                    v-else-if="(current_page==='project-data')"
                    :record="record"
                    :editable="editable"
                    :activatable="activatable"
                    :wizard-shown="!!page_wizard"
                    @wizard="toggle_page_wizard"
                    @status-change-done="reset_status_ui"
                    class="uk-active"
                />
                <client-data-tab
                    v-else-if="(current_page==='client-data')"
                    :record="record"
                    :editable="editable"
                    :wizard-shown="!!page_wizard"
                    class="uk-active"
                />
            </keep-alive>
        </ul>

        <div v-if="!!page_wizard" class="uk-notify lam-page-wizard">
            <div class="uk-notify-message uk-notify-message-warning">
                <a class="uk-close" @click.prevent="cancel_wizard"></a>
                <div class="uk-grid uk-grid-small lam-bordered-columns" data-uk-grid-margin>
                    <div class="uk-width-4-5 uk-text-center">
                        <strong>Missing Required Information</strong>
                        <div class="uk-margin-small-top uk-margin-small-bottom">
                            Before you can change this project's status to <strong>"{{ page_wizard }}"</strong>, it is strongly recommended you complete the following pieces of information
                            <br><small>(Click on any of the checkboxes to the right to get to that section quickly)</small>
                        </div>
                        <base-button color="primary" width="1-1" class="uk-width-1-2" @click.prevent="cancel_wizard">Cancel Status Change</base-button>
                    </div>
                    <div class="uk-width-1-5">
                        <ul class="uk-list">
                            <li>
                                <a href @click.prevent="wizard_scroll_to('#project-type-title', 'project-data')">
                                    <i :class="{'uk-icon-square-o': !has_required_data('category_ids'), 'uk-icon-check-square-o' : has_required_data('category_ids')}"></i> Project Type
                                </a>
                            </li>
                            <li v-if="!wizard_ignore_project_size">
                                <a href @click.prevent="wizard_scroll_to('#project-size-title', 'project-data')">
                                    <i :class="{'uk-icon-square-o': !has_required_data('size'), 'uk-icon-check-square-o' : has_required_data('size')}"></i> Project Size
                                </a>
                            </li>
                            <li>
                                <a href @click.prevent="wizard_scroll_to('#project-address-container')">
                                    <i :class="{'uk-icon-square-o': !has_required_data('address'), 'uk-icon-check-square-o' : has_required_data('address')}"></i> Address
                                </a>
                            </li>
                            <li>
                                <a href @click.prevent="wizard_scroll_to('#project-main-contact-title', 'client-data')">
                                    <i :class="{'uk-icon-square-o': !has_required_data('main_contact'), 'uk-icon-check-square-o' : has_required_data('main_contact')}"></i> Main Contact
                                </a>
                            </li>
                            <!--
                            <li>
                                <a href @click.prevent="wizard_scroll_to('#project-starts-at', 'prospective-data')">
                                    <i :class="{'uk-icon-square-o': !has_required_data('start_date'), 'uk-icon-check-square-o' : has_required_data('start_date')}"></i> Project Start Date
                                </a>
                            </li>
                            //-->
                            <li>
                                <a href @click.prevent="wizard_scroll_to('#prospective-phases', 'prospective-data')">
                                    <i :class="{'uk-icon-square-o': !has_required_data('phase_fees')||!has_required_data('phase_dates'), 'uk-icon-check-square-o' : has_required_data('phase_fees')&&has_required_data('phase_dates')}"></i> Phase Fees and Dates
                                </a>
                            </li>
                        </ul>
                        <div class="uk-text-right uk-margin-top">
                            <button type="button" class="uk-button uk-button-link uk-button-mini" @click.prevent="wizard_override">
                                Proceed without the missing information
                                <i class="uk-icon-arrow-right"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_project from "./mixins/IsProject"
import wizard_target from "./mixins/WizardTarget"
import profile_utilities from "../../mixins/ProfileUtilities"

import BaseButton from "../../ui/BaseButton"

export default {
    mixins: [is_project, wizard_target, profile_utilities],
    components: {
        BaseButton
    },
    watch: {
        activatable: "auto_close_wizard"
    },
    methods: {
        toggle_page_wizard (new_status) {
            if(this.page_wizard !== new_status) {
                this.page_wizard = new_status
                if(this.page_wizard) {
                    if(!this.has_required_data("category_ids")) this.wizard_scroll_to("#project-type-title", "project-data")
                    else if(!this.has_required_data("size")) this.wizard_scroll_to("#project-size-title", "project-data")
                    else if(!this.has_required_data("address")) this.wizard_scroll_to("#project-address-container", "project-data")
                    else if(!this.has_required_data("main_contact")) this.wizard_scroll_to("#project-main-contact-title", "client-data")
                    else if(!this.has_required_data("phase_fees") || !this.has_required_data("phase_dates")) this.wizard_scroll_to("#prospective-phases", "prospective-data")
                }
            }
        },
        auto_close_wizard () {
            if(this.project && this.page_wizard && this.activatable) {
                this.$nextTick(() => this.cancel_wizard())
            }
        },
        cancel_wizard () {
            this.toggle_page_wizard(null)
        },
        wizard_scroll_to (selector, tab_id) {
            const scroll_to = () => {
                // eslint-disable-next-line
                this.scroll_to(selector, Math.max(UIkit.$(".lam-page-wizard").outerHeight(true), 210))
            }
            if(!!tab_id && (tab_id !== this.current_page)) this.current_page = tab_id
            setTimeout(() => {
                scroll_to()
            }, 300)
        },
        wizard_override () {
            this.force_activate = true
            this.toggle_page_wizard(null)
            this.wizard_scroll_to("#status-picker", "project-data")
        },
        switched_current_page (page) {
            this.current_page = page
        },
        reset_status_ui () {
            this.force_activate = false
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        activatable () {
            if(this.force_activate) return true
            if(!this.has_required_data("category_ids")) return false
            if(!this.has_required_data("size")) return false
            if(!this.has_required_data("address")) return false
            if(!this.has_required_data("main_contact")) return false
            if(!this.has_required_data("phase_fees") || !this.has_required_data("phase_dates")) return false
            // Project Type, Project Size, Address, Main Project Contact, Phase Builder- Fee and Dates
            return true
        }
    },
    props: {
        editable: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            force_activate: false,
            current_page: "prospective-data"
        }
    }
}
</script>
